/**
 * MetLife Stadium geometry + section inventory for the SVG stadium map —
 * a replica of the official Giants seating chart.
 *
 * Orientation matches the chart:
 *   - Field runs horizontally; west end zone on the LEFT, east on the RIGHT.
 *   - Giants sideline at the TOP, visitors at the bottom.
 *   - Section numbers ascend CLOCKWISE from the west end zone.
 *
 * The bowl is NOT uniformly spaced — end-zone sections are wider than
 * sideline sections — so every ring is pinned to the chart at all four
 * cardinal points and interpolated per quadrant:
 *   - Lower:  101 dead-center west · 113 top · 126 dead-center east · 139 bottom
 *   - Mezz:   201 west · 213 top · 226 east · 239 bottom
 *   - Upper:  301 west · 313|314 top · 326 east · 338|339 bottom
 *
 * Lettered pairs behave like the chart: 202A/B, 203A/B, 224A/B, 225A/B,
 * 227A/B, 228A/B, 249A/B, 250A/B are RADIAL splits (A in front, B behind);
 * 111A/C, 115A/C, 207A/C, 220A/C, 232A/C, 245A/C are narrow LATERAL slices.
 * Decorative chart furniture — suite-level bands (3/5/6 East & West), the
 * Corona Beach / Miller Lite club strips, the four corner suites
 * (205B/222B/230B/246B), and gate stubs — is exported via STADIUM_DECOR.
 */

export const MAP_W = 1000;
export const MAP_H = 780;
export const MAP_CX = MAP_W / 2;
export const MAP_CY = MAP_H / 2;

export type LevelId = 'lower' | 'mezz' | 'upper';

export const LEVEL_NAME: Record<LevelId, string> = {
  lower: 'Lower Level',
  mezz: 'Mezzanine Level',
  upper: 'Upper Level',
};

/** Pale ring tints for sections with no active listings (chart-style). */
export const LEVEL_FILL: Record<LevelId, string> = {
  lower: '#bccdd4',
  mezz: '#c3cbe9',
  upper: '#c2d8ec',
};

export const FIELD = { x: MAP_CX - 152, y: MAP_CY - 68, w: 304, h: 136 };

type SectionDef = {
  id: string;
  /** Lateral width in units (0.5 = half-width slice like 111A). */
  w?: number;
  /** Radially-behind partner (B section drawn between this cell and the ring's outer edge). */
  outer?: string;
};

type AnchorDef = { id: string; at: 'center' | 'end'; deg: 0 | 90 | 180 | 270 };

type RingDef = {
  level: LevelId;
  rxIn: number;
  ryIn: number;
  rxOut: number;
  ryOut: number;
  /** Superellipse exponent — higher = squarer. 2 is a pure ellipse. */
  n: number;
  sections: SectionDef[];
  anchors: AnchorDef[];
};

const HALF = 0.5;

const LOWER: SectionDef[] = [
  { id: '101' }, { id: '103' }, { id: '104' }, { id: '106' }, { id: '108' },
  { id: '109' }, { id: '110' },
  { id: '111A', w: HALF }, { id: '111C', w: HALF },
  { id: '112' }, { id: '113' }, { id: '114' },
  { id: '115C', w: HALF }, { id: '115A', w: HALF },
  { id: '116' }, { id: '117' }, { id: '118' }, { id: '121' }, { id: '123' },
  { id: '124' }, { id: '126' }, { id: '128' }, { id: '129' }, { id: '131' },
  { id: '133' }, { id: '134' }, { id: '135' }, { id: '137' }, { id: '139' },
  { id: '140' }, { id: '142' }, { id: '143' }, { id: '144' }, { id: '146' },
  { id: '148' }, { id: '149' },
];

const MEZZ: SectionDef[] = [
  { id: '201' },
  { id: '202A', outer: '202B' }, { id: '203A', outer: '203B' },
  { id: '204' }, { id: '205A' }, { id: '206A' },
  { id: '207A', w: HALF }, { id: '207C', w: HALF },
  { id: '208' }, { id: '209' }, { id: '210' }, { id: '211' }, { id: '212' },
  { id: '213' }, { id: '215' }, { id: '216' }, { id: '217' }, { id: '218' },
  { id: '219' },
  { id: '220C', w: HALF }, { id: '220A', w: HALF },
  { id: '221A' }, { id: '222A' }, { id: '223' },
  { id: '224A', outer: '224B' }, { id: '225A', outer: '225B' },
  { id: '226' },
  { id: '227A', outer: '227B' }, { id: '228A', outer: '228B' },
  { id: '229' }, { id: '230A' }, { id: '231A' },
  { id: '232A', w: HALF }, { id: '232C', w: HALF },
  { id: '233' }, { id: '234' }, { id: '235' }, { id: '236' }, { id: '237' },
  { id: '239' }, { id: '240' }, { id: '241' }, { id: '242' }, { id: '243' },
  { id: '244' },
  { id: '245A', w: HALF }, { id: '245C', w: HALF },
  { id: '246A' }, { id: '247A' }, { id: '248' },
  { id: '249A', outer: '249B' }, { id: '250A', outer: '250B' },
];

const UPPER: SectionDef[] = Array.from({ length: 50 }, (_, i) => ({
  id: String(301 + i),
}));

const RINGS: RingDef[] = [
  {
    level: 'lower',
    rxIn: 198, ryIn: 100, rxOut: 278, ryOut: 170, n: 2.15,
    sections: LOWER,
    anchors: [
      { id: '101', at: 'center', deg: 180 },
      { id: '113', at: 'center', deg: 270 },
      { id: '126', at: 'center', deg: 0 },
      { id: '139', at: 'center', deg: 90 },
    ],
  },
  {
    level: 'mezz',
    rxIn: 304, ryIn: 194, rxOut: 354, ryOut: 240, n: 2.5,
    sections: MEZZ,
    anchors: [
      { id: '201', at: 'center', deg: 180 },
      { id: '213', at: 'center', deg: 270 },
      { id: '226', at: 'center', deg: 0 },
      { id: '239', at: 'center', deg: 90 },
    ],
  },
  {
    level: 'upper',
    rxIn: 414, ryIn: 294, rxOut: 486, ryOut: 358, n: 2.95,
    sections: UPPER,
    anchors: [
      { id: '301', at: 'center', deg: 180 },
      { id: '313', at: 'end', deg: 270 },
      { id: '326', at: 'center', deg: 0 },
      { id: '338', at: 'end', deg: 90 },
    ],
  },
];

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

function superPt(rx: number, ry: number, deg: number, n: number): [number, number] {
  const a = (deg * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  const e = 2 / n;
  return [
    MAP_CX + rx * Math.sign(c) * Math.abs(c) ** e,
    MAP_CY + ry * Math.sign(s) * Math.abs(s) ** e,
  ];
}

const ARC_STEPS = 1440;

/** Cumulative arc length around a superellipse, starting at 180° (west). */
function buildArcTable(rx: number, ry: number, n: number): number[] {
  const cum: number[] = [0];
  let prev = superPt(rx, ry, 180, n);
  for (let i = 1; i <= ARC_STEPS; i++) {
    const p = superPt(rx, ry, 180 + (360 * i) / ARC_STEPS, n);
    cum.push(cum[i - 1]! + Math.hypot(p[0] - prev[0], p[1] - prev[1]));
    prev = p;
  }
  return cum;
}

/** Perimeter fraction (0 at west, clockwise) → superellipse parameter angle. */
function fracToDeg(cum: number[], frac: number): number {
  const total = cum[cum.length - 1]!;
  const t = (((frac % 1) + 1) % 1) * total;
  let lo = 0;
  let hi = cum.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (cum[mid]! < t) lo = mid + 1;
    else hi = mid;
  }
  const i = Math.max(1, lo);
  const seg = cum[i]! - cum[i - 1]! || 1;
  const within = (t - cum[i - 1]!) / seg;
  return 180 + ((i - 1 + within) * 360) / ARC_STEPS;
}

/** Sampled polygon path for an annular sector, plus its bbox. */
function annularPath(
  rxIn: number, ryIn: number, rxOut: number, ryOut: number,
  n: number, d1: number, d2: number
): { path: string; bbox: { x: number; y: number; w: number; h: number } } {
  const k = Math.max(3, Math.ceil((d2 - d1) / 2.5) + 1);
  const outer: [number, number][] = [];
  const inner: [number, number][] = [];
  for (let i = 0; i < k; i++) {
    const d = d1 + ((d2 - d1) * i) / (k - 1);
    outer.push(superPt(rxOut, ryOut, d, n));
    inner.push(superPt(rxIn, ryIn, d, n));
  }
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of [...outer, ...inner]) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  const path =
    `M ${outer.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(' L ')}` +
    ` L ${inner.reverse().map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(' L ')} Z`;
  return { path, bbox: { x: minX, y: minY, w: maxX - minX, h: maxY - minY } };
}

/** Quadrant fraction each cardinal parameter angle maps to. */
const DEG_FRAC: Record<number, number> = { 180: 0, 270: 0.25, 0: 0.5, 90: 0.75 };

export type SectionCell = {
  id: string;
  level: LevelId;
  path: string;
  label: [number, number];
  labelSize: number;
  /** Degrees to rotate the label (narrow cells get radial labels, chart-style). */
  labelRotate: number;
  /** Parameter angle at the cell's center, normalised to [0, 360). */
  midDeg: number;
  /** Cell's angular span (a2 may exceed 360 when the cell wraps past west). */
  a1: number;
  a2: number;
  bbox: { x: number; y: number; w: number; h: number };
};

function buildRing(ring: RingDef): SectionCell[] {
  const weights = ring.sections.map((s) => s.w ?? 1);
  const totalU = weights.reduce((a, b) => a + b, 0);
  const midRx = (ring.rxIn + ring.rxOut) / 2;
  const midRy = (ring.ryIn + ring.ryOut) / 2;
  const cum = buildArcTable(midRx, midRy, ring.n);
  const perimeter = cum[cum.length - 1]!;

  // Cumulative unit position where each section starts.
  const starts: number[] = [];
  let acc = 0;
  for (const w of weights) {
    starts.push(acc);
    acc += w;
  }

  // Pin the ring to the chart at each cardinal anchor, then interpolate
  // units → perimeter fraction linearly within each quadrant.
  const anchors = ring.anchors
    .map((a) => {
      const i = ring.sections.findIndex((s) => s.id === a.id);
      const u = starts[i]! + (a.at === 'center' ? weights[i]! / 2 : weights[i]!);
      return { u, frac: DEG_FRAC[a.deg]! };
    })
    .sort((a, b) => a.u - b.u);

  function unitToFrac(u: number): number {
    let x = u;
    if (x < anchors[0]!.u) x += totalU;
    for (let k = 0; k < anchors.length; k++) {
      const a = anchors[k]!;
      const b =
        k + 1 < anchors.length
          ? anchors[k + 1]!
          : { u: anchors[0]!.u + totalU, frac: anchors[0]!.frac + 1 };
      if (x >= a.u && x <= b.u) {
        return (a.frac + ((x - a.u) / (b.u - a.u)) * (b.frac - a.frac)) % 1;
      }
    }
    return anchors[0]!.frac;
  }

  const cells: SectionCell[] = [];
  for (let i = 0; i < ring.sections.length; i++) {
    const def = ring.sections[i]!;
    const f1 = unitToFrac(starts[i]!);
    const f2 = unitToFrac(starts[i]! + weights[i]!);
    const span = (f2 - f1 + 1) % 1;
    const gapF = Math.min(0.0032, span * 0.14);

    const d1 = fracToDeg(cum, f1 + gapF / 2);
    let d2 = fracToDeg(cum, f1 + span - gapF / 2);
    if (d2 <= d1) d2 += 360;
    const midDeg = ((d1 + d2) / 2) % 360;
    const arcW = (span - gapF) * perimeter;

    // A/B pairs are radial: A takes the front of the ring, B the back.
    const split = def.outer ? 0.55 : 1;
    const rxMid = ring.rxIn + (ring.rxOut - ring.rxIn) * split;
    const ryMid = ring.ryIn + (ring.ryOut - ring.ryIn) * split;
    const bands: { id: string; rxI: number; ryI: number; rxO: number; ryO: number }[] = [
      { id: def.id, rxI: ring.rxIn, ryI: ring.ryIn, rxO: rxMid, ryO: ryMid },
    ];
    if (def.outer) {
      bands.push({ id: def.outer, rxI: rxMid, ryI: ryMid, rxO: ring.rxOut, ryO: ring.ryOut });
    }

    for (const b of bands) {
      const { path, bbox } = annularPath(b.rxI, b.ryI, b.rxO, b.ryO, ring.n, d1, d2);
      const labelR: [number, number] = [(b.rxI + b.rxO) / 2, (b.ryI + b.ryO) / 2];
      const labelPt = superPt(labelR[0], labelR[1], (d1 + d2) / 2, ring.n);
      const [ox, oy] = superPt(b.rxO, b.ryO, (d1 + d2) / 2, ring.n);
      const [ix, iy] = superPt(b.rxI, b.ryI, (d1 + d2) / 2, ring.n);
      const thickness = Math.hypot(ox - ix, oy - iy);

      // Cells too narrow for a horizontal label get one rotated to run
      // radially (inner edge → outer edge), like the official chart.
      const narrow = arcW < 26;
      let labelRotate = 0;
      if (narrow) {
        labelRotate = midDeg;
        if (labelRotate > 90 && labelRotate <= 270) labelRotate -= 180;
      }
      const labelSize = narrow
        ? Math.max(6.5, Math.min(arcW * 0.75, thickness / 3, 10))
        : Math.max(7, Math.min(arcW * 0.32, thickness * 0.45, 13));

      cells.push({
        id: b.id,
        level: ring.level,
        path,
        label: labelPt,
        labelSize,
        labelRotate,
        midDeg,
        a1: d1,
        a2: d2,
        bbox,
      });
    }
  }
  return cells;
}

export const STADIUM_CELLS: SectionCell[] = RINGS.flatMap(buildRing);

export const CELL_BY_ID: Map<string, SectionCell> = new Map(
  STADIUM_CELLS.map((c) => [c.id, c])
);

export const ALL_SECTION_IDS: string[] = STADIUM_CELLS.map((c) => c.id);

// ---------------------------------------------------------------------------
// Chart furniture: suite bands, club strips, corner suites, gate stubs
// ---------------------------------------------------------------------------

export type DecorBand = {
  path: string;
  fill: string;
  label?: string;
  labelPos?: [number, number];
  labelSize?: number;
  endLabels?: { text: string; pos: [number, number] }[];
};

export type DecorSuite = {
  id: string;
  path: string;
  label: [number, number];
  labelSize: number;
};

function deg(id: string, edge: 'a1' | 'a2' | 'mid'): number {
  const c = CELL_BY_ID.get(id)!;
  if (edge === 'mid') return (c.a1 + c.a2) / 2;
  return c[edge];
}

const SUITE_R = { rxIn: 360, ryIn: 246, rxOut: 408, ryOut: 288, n: 2.7 };

/** Corner-suite angular spans: each sits over two mezzanine corner sections. */
const SUITE_SPAN: Record<string, [number, number]> = {
  '205B': [deg('205A', 'a1') - 4, deg('206A', 'a2') + 4], // NW
  '222B': [deg('221A', 'a1') - 4, deg('222A', 'a2') + 4], // NE
  '230B': [deg('230A', 'a1') - 4, deg('231A', 'a2') + 4], // SE
  '246B': [deg('246A', 'a1') - 4, deg('247A', 'a2') + 4], // SW
};

function cornerSuite(id: string): DecorSuite {
  const [d1, d2] = SUITE_SPAN[id]!;
  const { path } = annularPath(
    SUITE_R.rxIn, SUITE_R.ryIn, SUITE_R.rxOut, SUITE_R.ryOut, SUITE_R.n, d1, d2
  );
  const mid = (d1 + d2) / 2;
  return {
    id,
    path,
    label: superPt((SUITE_R.rxIn + SUITE_R.rxOut) / 2, (SUITE_R.ryIn + SUITE_R.ryOut) / 2, mid, SUITE_R.n),
    labelSize: 11,
  };
}

type BandSpec = {
  rxIn: number; ryIn: number; rxOut: number; ryOut: number; n: number;
  d1: number; d2: number; fill: string;
  label?: string; ends?: [string, string]; center: 270 | 90;
};

function band(spec: BandSpec): DecorBand {
  const { path } = annularPath(spec.rxIn, spec.ryIn, spec.rxOut, spec.ryOut, spec.n, spec.d1, spec.d2);
  const midR: [number, number] = [(spec.rxIn + spec.rxOut) / 2, (spec.ryIn + spec.ryOut) / 2];
  const out: DecorBand = { path, fill: spec.fill };
  if (spec.label) {
    out.label = spec.label;
    out.labelPos = superPt(midR[0], midR[1], spec.center, spec.n);
    out.labelSize = 8;
  }
  if (spec.ends) {
    out.endLabels = [
      { text: spec.ends[0], pos: superPt(midR[0], midR[1], spec.d1 + 4, spec.n) },
      { text: spec.ends[1], pos: superPt(midR[0], midR[1], spec.d2 - 4, spec.n) },
    ];
  }
  return out;
}

function buildDecor(): { bands: DecorBand[]; suites: DecorSuite[]; gates: string[] } {
  const suites = ['205B', '222B', '230B', '246B'].map(cornerSuite);
  const nw = SUITE_SPAN['205B']!;
  const ne = SUITE_SPAN['222B']!;
  const se = SUITE_SPAN['230B']!;
  const sw = SUITE_SPAN['246B']!;

  const bands: DecorBand[] = [
    // Suite Level 3 — between the lower bowl and the mezzanine row.
    band({
      rxIn: 284, ryIn: 174, rxOut: 298, ryOut: 188, n: 2.3,
      d1: deg('208', 'a1'), d2: deg('219', 'a2'),
      fill: '#7b2d52', label: 'SUITE LEVEL 3 EAST', ends: ['3-01', '3-30'], center: 270,
    }),
    band({
      rxIn: 284, ryIn: 174, rxOut: 298, ryOut: 188, n: 2.3,
      d1: deg('233', 'a1'), d2: deg('244', 'a2'),
      fill: '#7b2d52', label: 'SUITE LEVEL 3 WEST', ends: ['3-50', '3-80'], center: 90,
    }),
    // Club strips just outside the mezzanine row.
    band({
      rxIn: 358, ryIn: 244, rxOut: 370, ryOut: 254, n: 2.6,
      d1: deg('209', 'a1'), d2: deg('218', 'a2'),
      fill: '#64748b', label: 'CORONA BEACH CLUB', center: 270,
    }),
    band({
      rxIn: 358, ryIn: 244, rxOut: 370, ryOut: 254, n: 2.6,
      d1: deg('234', 'a1'), d2: deg('243', 'a2'),
      fill: '#11635d', label: 'MILLER LITE MVP CLUB', center: 90,
    }),
    // Suite Level 5.
    band({
      rxIn: 374, ryIn: 258, rxOut: 388, ryOut: 270, n: 2.7,
      d1: deg('208', 'a1') - 4, d2: deg('219', 'a2') + 4,
      fill: '#5b21a8', label: 'SUITE LEVEL 5 EAST', ends: ['5-01', '5-39'], center: 270,
    }),
    band({
      rxIn: 374, ryIn: 258, rxOut: 388, ryOut: 270, n: 2.7,
      d1: deg('233', 'a1') - 4, d2: deg('244', 'a2') + 4,
      fill: '#5b21a8', label: 'SUITE LEVEL 5 WEST', ends: ['5-50', '5-88'], center: 90,
    }),
    // Suite Level 6 — runs corner-suite to corner-suite.
    band({
      rxIn: 392, ryIn: 274, rxOut: 406, ryOut: 286, n: 2.75,
      d1: nw[1] + 1.5, d2: ne[0] - 1.5,
      fill: '#343064', label: 'SUITE LEVEL 6 EAST', ends: ['6-01', '6-39'], center: 270,
    }),
    band({
      rxIn: 392, ryIn: 274, rxOut: 406, ryOut: 286, n: 2.75,
      d1: se[1] + 1.5, d2: sw[0] - 1.5,
      fill: '#343064', label: 'SUITE LEVEL 6 WEST', ends: ['6-50', '6-67'], center: 90,
    }),
  ];

  // Gate stubs on the outer edge (AMEX top, MetLife bottom, plus corners).
  const gates: string[] = [270, 90, 315, 45, 135].map(
    (d) =>
      annularPath(
        RINGS[2]!.rxOut + 3, RINGS[2]!.ryOut + 3,
        RINGS[2]!.rxOut + 14, RINGS[2]!.ryOut + 14,
        RINGS[2]!.n, d - 3.5, d + 3.5
      ).path
  );

  return { bands, suites, gates };
}

export const STADIUM_DECOR = buildDecor();

// ---------------------------------------------------------------------------
// Section metadata helpers
// ---------------------------------------------------------------------------

export type Zone =
  | 'sideline-home'
  | 'sideline-visitor'
  | 'endzone-west'
  | 'endzone-east'
  | 'corner-nw'
  | 'corner-ne'
  | 'corner-se'
  | 'corner-sw';

const ZONE_LABEL: Record<Zone, string> = {
  'sideline-home': 'Giants sideline',
  'sideline-visitor': 'Visitors sideline',
  'endzone-west': 'West end zone',
  'endzone-east': 'East end zone',
  'corner-nw': 'West corner, Giants side',
  'corner-ne': 'East corner, Giants side',
  'corner-se': 'East corner, visitors side',
  'corner-sw': 'West corner, visitors side',
};

export function levelOf(sectionId: string): LevelId | null {
  const ch = sectionId.trim()[0];
  if (ch === '1') return 'lower';
  if (ch === '2') return 'mezz';
  if (ch === '3') return 'upper';
  return null;
}

/** Where a section sits around the bowl (top of map = Giants sideline). */
export function zoneOf(sectionId: string): Zone | null {
  const cell = CELL_BY_ID.get(sectionId.trim().toUpperCase());
  if (!cell) return null;
  const d = ((cell.midDeg % 360) + 360) % 360;
  if (d >= 250 && d <= 290) return 'sideline-home';
  if (d >= 70 && d <= 110) return 'sideline-visitor';
  if (d >= 160 && d <= 200) return 'endzone-west';
  if (d <= 20 || d >= 340) return 'endzone-east';
  if (d > 200 && d < 250) return 'corner-nw';
  if (d > 290 && d < 340) return 'corner-ne';
  if (d > 20 && d < 70) return 'corner-se';
  return 'corner-sw';
}

/** Human-readable location, e.g. "Lower Level · Giants sideline". */
export function describeSection(sectionId: string): string {
  const level = levelOf(sectionId);
  const zone = zoneOf(sectionId);
  const parts: string[] = [];
  if (level) parts.push(LEVEL_NAME[level]);
  if (zone) parts.push(ZONE_LABEL[zone]);
  return parts.join(' · ');
}
